import React, { useEffect, useState } from 'react';
import { SettingsIcon, BuildingIcon, UploadIcon, Trash2Icon, ChevronDownIcon, SaveIcon, CheckCircleIcon } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
type TabType = 'dealer' | 'design' | 'quote';
type DealerSubTabType = 'legal' | 'address' | 'contact';
export function SystemSettingsPage() {
  const {
    role,
    companyName,
    companyLogo,
    setCompanyLogo,
    officialCountry,
    setOfficialCountry
  } = useUser();
  const [activeTab, setActiveTab] = useState<TabType>('dealer');
  const [dealerSubTab, setDealerSubTab] = useState<DealerSubTabType>('legal');
  const [logoPreview, setLogoPreview] = useState<string | null>(companyLogo);
  // Legal data
  const [legalName, setLegalName] = useState('DAIRY GLOBAL EXPERTS LLC LN');
  const [taxId, setTaxId] = useState('INN 392949923233');
  const [billingCountry, setBillingCountry] = useState('');
  // Billing address
  const [lineAddress1, setLineAddress1] = useState('50 Centralna st');
  const [lineAddress2, setLineAddress2] = useState('');
  const [city, setCity] = useState('Hromy village, Uman district');
  const [stateProvince, setStateProvince] = useState('Cherkasy Region');
  const [zipCode, setZipCode] = useState('20343');
  const [distributionCountry, setDistributionCountry] = useState('Ukraine');
  // Contact
  const [phone, setPhone] = useState('+380 97 410 2423');
  const [email, setEmail] = useState('irrigation.dge@gmail.com');
  const [url, setUrl] = useState('www.dge.com.ua');
  const [dealerMarkup, setDealerMarkup] = useState('25.00');
  // Design Preferences - Irrigation Capacity
  const [applicationRate, setApplicationRate] = useState('1.30');
  const [applicationRateUnit, setApplicationRateUnit] = useState('l/s/ha');
  const [irrigationTimingMode, setIrrigationTimingMode] = useState<'hours' | 'percentage'>('hours');
  const [hoursPerDay, setHoursPerDay] = useState('18');
  const [daysPerWeek, setDaysPerWeek] = useState('7');
  const [percentageRate, setPercentageRate] = useState('75.00');
  // Design Preferences - General Configuration
  const [brand, setBrand] = useState('Pierce');
  const [model, setModel] = useState('CP600');
  const [electricalFrequency, setElectricalFrequency] = useState('60 Hz');
  const [profile, setProfile] = useState('Standard');
  // Design Preferences - Controls
  const [communicationType, setCommunicationType] = useState('GSM 4G');
  const [temperatureSensor, setTemperatureSensor] = useState(true);
  const [dosingPumpControl, setDosingPumpControl] = useState(false);
  const [stopInSlot, setStopInSlot] = useState(false);
  const [lightingArrestor, setLightingArrestor] = useState(false);
  // Design Preferences - Pivot Point
  const [pivotProfile, setPivotProfile] = useState('Standard');
  const [pivotProtectiveCoating, setPivotProtectiveCoating] = useState('Galvanized');
  const [standPipeDiameter, setStandPipeDiameter] = useState('8"5/8');
  const [heavyDuty, setHeavyDuty] = useState(false);
  // Design Preferences - Spans
  const [outletSpacing, setOutletSpacing] = useState('1.5 m');
  const [spansProtectiveCoating, setSpansProtectiveCoating] = useState('Galvanized');
  const [spansProfile, setSpansProfile] = useState('Standard');
  // Design Preferences - Drive Train
  const [motorCover, setMotorCover] = useState(true);
  const [centerDrive, setCenterDrive] = useState('UMC 35/2...');
  const [wdGearbox, setWdGearbox] = useState('Std. 52/1');
  const [tires, setTires] = useState('14.9x24');
  // Design Preferences - Electric Cables
  const [cableProtection, setCableProtection] = useState('Unshield');
  const [wireSize, setWireSize] = useState('4 mm²');
  // Design Preferences - Sprinklers
  const [maxSprinklerSpacing, setMaxSprinklerSpacing] = useState('1.50');
  const [sprinklerName, setSprinklerName] = useState('Nelson | Rotator-R3...');
  const [pad, setPad] = useState('Orange');
  const [drops, setDrops] = useState('Flex Drop with plast...');
  const [clearanceHeight, setClearanceHeight] = useState('2.60');
  const [regulator, setRegulator] = useState(true);
  const [regulatorBrand, setRegulatorBrand] = useState('Komet');
  const [nominalPressure, setNominalPressure] = useState('10 psi');
  const [verticalPosition, setVerticalPosition] = useState('Top');
  // Design Preferences - End Gun 1
  const [endGun1Brand, setEndGun1Brand] = useState('Komet');
  const [endGun1Model, setEndGun1Model] = useState('KPW HEAD-UP');
  const [endGun1Control, setEndGun1Control] = useState('Endgun Control wit...');
  // Design Preferences - End Gun 2
  const [endGun2Brand, setEndGun2Brand] = useState('');
  const [endGun2Model, setEndGun2Model] = useState('');
  const [endGun2Control, setEndGun2Control] = useState('');
  // Design Preferences - Booster Pump
  const [boosterPumpPower, setBoosterPumpPower] = useState('');
  // Save status tracking
  const [savingTab, setSavingTab] = useState<string | null>(null);
  const [savedTab, setSavedTab] = useState<string | null>(null);
  // Initialize state
  useEffect(() => {
    setLogoPreview(companyLogo);
  }, [companyLogo]);
  // Determine if user can edit logo and official country
  const canEditLogo = ['sales-manager', 'operations-manager', 'manager-business-unit', 'ceo', 'logistic', 'marketing', 'financial-manager', 'system-admin', 'dealer-admin', 'region-admin'].includes(role);
  const canEditOfficialCountry = canEditLogo;
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  const handleRemoveLogo = () => {
    setLogoPreview(null);
  };
  const handleSaveDealerInfo = () => {
    setSavingTab('dealer');
    setCompanyLogo(logoPreview);
    setTimeout(() => {
      setSavingTab(null);
      setSavedTab('dealer');
      setTimeout(() => setSavedTab(null), 2000);
    }, 600);
  };
  const handleSaveDesignPreferences = () => {
    setSavingTab('design');
    setTimeout(() => {
      setSavingTab(null);
      setSavedTab('design');
      setTimeout(() => setSavedTab(null), 2000);
    }, 600);
  };
  const handleSaveQuote = () => {
    setSavingTab('quote');
    setTimeout(() => {
      setSavingTab(null);
      setSavedTab('quote');
      setTimeout(() => setSavedTab(null), 2000);
    }, 600);
  };
  const handleSave = () => {
    setCompanyLogo(logoPreview);
    // In a real app, we would save all the other fields here too
    alert('Settings saved successfully!');
  };
  const countries = ['México', 'Estados Unidos', 'España', 'Colombia', 'Argentina', 'Brasil', 'Chile', 'Perú', 'Alemania', 'Francia', 'Ucrania', 'Ukraine', 'Reino Unido', 'Italia', 'Canadá'];
  const tabs = [{
    id: 'dealer' as TabType,
    label: 'Dealer Information'
  }, {
    id: 'design' as TabType,
    label: 'Design Preferences'
  }, {
    id: 'quote' as TabType,
    label: 'Quote'
  }];
  const dealerSubTabs = [{
    id: 'legal' as DealerSubTabType,
    label: 'Legal Data'
  }, {
    id: 'address' as DealerSubTabType,
    label: 'Billing Address'
  }, {
    id: 'contact' as DealerSubTabType,
    label: 'Contact'
  }];
  return <div className="flex flex-col h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950" data-id="element-10316">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-10317">
        <div className="absolute top-[10%] left-[5%] w-64 h-64 bg-blue-600 rounded-full mix-blend-multiply filter blur-[80px] opacity-20" data-id="element-10318"></div>
        <div className="absolute top-[40%] right-[10%] w-72 h-72 bg-purple-600 rounded-full mix-blend-multiply filter blur-[80px] opacity-20" data-id="element-10319"></div>
        <div className="absolute bottom-[10%] left-[25%] w-80 h-80 bg-teal-600 rounded-full mix-blend-multiply filter blur-[100px] opacity-20" data-id="element-10320"></div>
      </div>

      {/* Header */}
      <div className="bg-white/5 backdrop-blur-sm border-b border-white/10 px-6 py-4 relative z-10" data-id="element-10321">
        <div className="flex justify-between items-center" data-id="element-10322">
          <div className="flex items-center" data-id="element-10323">
            <h1 className="text-xl font-bold text-white flex items-center" data-id="element-10324">
              <SettingsIcon size={24} className="mr-2 text-blue-400" data-id="element-10325" />
              System Settings
            </h1>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white/5 backdrop-blur-sm border-b border-white/10 px-6 relative z-10" data-id="element-10326">
        <div className="flex" data-id="element-10327">
          {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-3 text-sm font-medium transition-colors relative ${activeTab === tab.id ? 'text-white' : 'text-blue-300 hover:text-white'}`} data-id="element-10328">
              {tab.label}
              {activeTab === tab.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500" data-id="element-10329" />}
            </button>)}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto p-6 relative z-10" data-id="element-10330">
        <div className="max-w-4xl mx-auto space-y-6" data-id="element-10331">
          {/* Dealer Information Tab */}
          {activeTab === 'dealer' && <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 overflow-hidden" data-id="element-10332">
              {/* Company Header Section */}
              <div className="px-6 py-4 border-b border-white/10" data-id="element-10333">
                <div className="flex items-center justify-between gap-4" data-id="element-10334">
                  {/* Left side: Logo and Company Name */}
                  <div className="flex items-center gap-4 flex-1 min-w-0" data-id="element-10335">
                    {/* Logo Section */}
                    <div className="relative flex-shrink-0" data-id="element-10336">
                      {logoPreview ? <div className="w-14 h-14 rounded-lg overflow-hidden bg-white/5 border border-white/10" data-id="element-10337">
                          <img src={logoPreview} alt="Company logo" className="w-full h-full object-cover" data-id="element-10338" />
                        </div> : <div className="w-14 h-14 rounded-lg bg-blue-600/20 flex items-center justify-center border border-white/10" data-id="element-10339">
                          <BuildingIcon size={28} className="text-blue-400" data-id="element-10340" />
                        </div>}

                      {/* Logo Upload/Remove Controls */}
                      {canEditLogo && <div className="absolute -bottom-1 -right-1 flex gap-1" data-id="element-10341">
                          <label className="cursor-pointer bg-blue-600 hover:bg-blue-700 text-white rounded-full p-1 shadow-lg border-2 border-[#0f1d3a] transition-colors" data-id="element-10342">
                            <UploadIcon size={10} data-id="element-10343" />
                            <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-id="element-10344" />
                          </label>
                          {logoPreview && <button onClick={handleRemoveLogo} className="bg-red-600 hover:bg-red-700 text-white rounded-full p-1 shadow-lg border-2 border-[#0f1d3a] transition-colors" data-id="element-10345">
                              <Trash2Icon size={10} data-id="element-10346" />
                            </button>}
                        </div>}
                    </div>

                    {/* Company Name */}
                    <div className="flex-1 min-w-0" data-id="element-10347">
                      <h3 className="text-lg font-semibold text-white truncate" data-id="element-10348">
                        {companyName}
                      </h3>
                    </div>
                  </div>

                  {/* Right side: Distribution Country */}
                  <div className="flex-shrink-0" data-id="element-10349">
                    <p className="text-xs text-blue-300 mb-1" data-id="element-10350">
                      Distribuidor oficial en
                    </p>
                    {canEditOfficialCountry ? <div className="relative" data-id="element-10351">
                        <select value={distributionCountry} onChange={e => setDistributionCountry(e.target.value)} className="appearance-none bg-white/10 border border-white/10 rounded-lg px-3 py-1.5 pr-8 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10352">
                          {countries.map(c => <option key={c} value={c} className="bg-slate-800 text-white" data-id="element-10353">
                              {c}
                            </option>)}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-blue-300" data-id="element-10354">
                          <ChevronDownIcon size={14} data-id="element-10355" />
                        </div>
                      </div> : <p className="text-sm font-medium text-white" data-id="element-10356">
                        {distributionCountry || 'No country set'}
                      </p>}
                  </div>
                </div>
              </div>

              {/* Sub Tabs */}
              <div className="flex border-b border-white/10 px-6" data-id="element-10357">
                {dealerSubTabs.map(tab => <button key={tab.id} onClick={() => setDealerSubTab(tab.id)} className={`px-4 py-3 text-sm font-medium transition-colors relative ${dealerSubTab === tab.id ? 'text-white' : 'text-blue-300 hover:text-white'}`} data-id="element-10358">
                    {tab.label}
                    {dealerSubTab === tab.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500" data-id="element-10359" />}
                  </button>)}
              </div>

              {/* Sub Tab Content */}
              <div className="p-6" data-id="element-10360">
                {/* Legal Data Sub Tab */}
                {dealerSubTab === 'legal' && <div className="space-y-5" data-id="element-10361">
                    <div data-id="element-10362">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10363">
                        Legal name <span className="text-red-400" data-id="element-10364">*</span>
                      </label>
                      <input type="text" value={legalName} onChange={e => setLegalName(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="Company legal name" data-id="element-10365" />
                    </div>

                    <div data-id="element-10366">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10367">
                        TAX ID / VAT ID / NIF{' '}
                        <span className="text-red-400" data-id="element-10368">*</span>
                      </label>
                      <input type="text" value={taxId} onChange={e => setTaxId(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="Tax identification number" data-id="element-10369" />
                    </div>
                  </div>}

                {/* Address Sub Tab */}
                {dealerSubTab === 'address' && <div className="space-y-5" data-id="element-10370">
                    <div data-id="element-10371">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10372">
                        Address line 1 <span className="text-red-400" data-id="element-10373">*</span>
                      </label>
                      <input type="text" value={lineAddress1} onChange={e => setLineAddress1(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="Street, number" data-id="element-10374" />
                    </div>

                    <div data-id="element-10375">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10376">
                        Address line 2
                      </label>
                      <input type="text" value={lineAddress2} onChange={e => setLineAddress2(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="Apartment, suite, etc. (optional)" data-id="element-10377" />
                    </div>

                    <div data-id="element-10378">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10379">
                        City <span className="text-red-400" data-id="element-10380">*</span>
                      </label>
                      <input type="text" value={city} onChange={e => setCity(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="City" data-id="element-10381" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-10382">
                      <div data-id="element-10383">
                        <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10384">
                          State / Province
                        </label>
                        <input type="text" value={stateProvince} onChange={e => setStateProvince(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="State or province" data-id="element-10385" />
                      </div>

                      <div data-id="element-10386">
                        <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10387">
                          Zip code
                        </label>
                        <input type="text" value={zipCode} onChange={e => setZipCode(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="Zip code" data-id="element-10388" />
                      </div>
                    </div>

                    <div data-id="element-10389">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10390">
                        Billing country <span className="text-red-400" data-id="element-10391">*</span>
                      </label>
                      <div className="relative" data-id="element-10392">
                        <select value={billingCountry} onChange={e => setBillingCountry(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10393">
                          <option value="" className="bg-slate-800 text-blue-300" data-id="element-10394">
                            Please, select country
                          </option>
                          {countries.map(c => <option key={c} value={c} className="bg-slate-800 text-white" data-id="element-10395">
                              {c}
                            </option>)}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10396">
                          <ChevronDownIcon size={16} data-id="element-10397" />
                        </div>
                      </div>
                      {!billingCountry && <p className="text-red-400 text-xs mt-1" data-id="element-10398">
                          Please, select country
                        </p>}
                    </div>
                  </div>}

                {/* Contact Sub Tab */}
                {dealerSubTab === 'contact' && <div className="space-y-5" data-id="element-10399">
                    <div data-id="element-10400">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10401">
                        Phone
                      </label>
                      <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="+1 234 567 8900" data-id="element-10402" />
                    </div>

                    <div data-id="element-10403">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10404">
                        Email
                      </label>
                      <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="company@email.com" data-id="element-10405" />
                    </div>

                    <div data-id="element-10406">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10407">
                        Website
                      </label>
                      <input type="url" value={url} onChange={e => setUrl(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="www.company.com" data-id="element-10408" />
                    </div>
                  </div>}
              </div>

              {/* Save Button for Dealer Information (common for all 3 sub-tabs) */}
              <div className="px-6 py-4 border-t border-white/10 flex items-center justify-end bg-black/10 rounded-b-xl" data-id="element-10409">
                <button onClick={handleSaveDealerInfo} disabled={savingTab === 'dealer'} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white rounded-lg px-5 py-2.5 text-sm font-medium transition-colors shadow-lg shadow-blue-900/20" data-id="element-10410">
                  {savingTab === 'dealer' ? <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-10411" />
                      Saving...
                    </> : savedTab === 'dealer' ? <>
                      <CheckCircleIcon size={16} data-id="element-10412" />
                      Saved!
                    </> : <>
                      <SaveIcon size={16} data-id="element-10413" />
                      Save Dealer Information
                    </>}
                </button>
              </div>
            </div>}

          {/* Design Preferences Tab */}
          {activeTab === 'design' && <div className="space-y-6" data-id="element-10414">
              {/* Irrigation Capacity Section */}
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-10415">
                <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-10416">
                  Irrigation Capacity
                </h3>
                <div className="space-y-5" data-id="element-10417">
                  <div data-id="element-10418">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10419">
                      Application rate
                    </label>
                    <div className="flex gap-3" data-id="element-10420">
                      <input type="number" step="0.01" value={applicationRate} onChange={e => setApplicationRate(e.target.value)} className="w-32 bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10421" />
                      <div className="relative flex-1" data-id="element-10422">
                        <select value={applicationRateUnit} onChange={e => setApplicationRateUnit(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10423">
                          <option value="l/s/ha" className="bg-slate-800 text-white" data-id="element-10424">
                            l/s/ha
                          </option>
                          <option value="mm/h" className="bg-slate-800 text-white" data-id="element-10425">
                            mm/h
                          </option>
                          <option value="gpm/ac" className="bg-slate-800 text-white" data-id="element-10426">
                            gpm/ac
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10427">
                          <ChevronDownIcon size={16} data-id="element-10428" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div data-id="element-10429">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10430">
                      Irrigation timing rate
                    </label>
                    <div className="space-y-3" data-id="element-10431">
                      <div className="flex items-center gap-3" data-id="element-10432">
                        <input type="radio" id="timing-hours" checked={irrigationTimingMode === 'hours'} onChange={() => setIrrigationTimingMode('hours')} className="w-4 h-4 text-blue-500 accent-blue-500" data-id="element-10433" />
                        <div className="flex items-center gap-2 flex-1" data-id="element-10434">
                          <input type="number" value={hoursPerDay} onChange={e => setHoursPerDay(e.target.value)} disabled={irrigationTimingMode !== 'hours'} className="w-20 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10435" />
                          <span className="text-white text-sm" data-id="element-10436">h/day</span>
                          <input type="number" value={daysPerWeek} onChange={e => setDaysPerWeek(e.target.value)} disabled={irrigationTimingMode !== 'hours'} className="w-20 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10437" />
                          <span className="text-white text-sm" data-id="element-10438">days/week</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-3" data-id="element-10439">
                        <input type="radio" id="timing-percentage" checked={irrigationTimingMode === 'percentage'} onChange={() => setIrrigationTimingMode('percentage')} className="w-4 h-4 text-blue-500 accent-blue-500" data-id="element-10440" />
                        <div className="flex items-center gap-2 flex-1" data-id="element-10441">
                          <input type="number" step="0.01" value={percentageRate} onChange={e => setPercentageRate(e.target.value)} disabled={irrigationTimingMode !== 'percentage'} className="w-24 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10442" />
                          <span className="text-white text-sm" data-id="element-10443">% 18h/24h</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* General Configuration Section */}
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-10444">
                <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-10445">
                  General Configuration
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5" data-id="element-10446">
                  <div data-id="element-10447">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10448">
                      Brand
                    </label>
                    <div className="relative" data-id="element-10449">
                      <select value={brand} onChange={e => setBrand(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10450">
                        <option value="Pierce" className="bg-slate-800 text-white" data-id="element-10451">
                          Pierce
                        </option>
                        <option value="Reinke" className="bg-slate-800 text-white" data-id="element-10452">
                          Reinke
                        </option>
                        <option value="Valley" className="bg-slate-800 text-white" data-id="element-10453">
                          Valley
                        </option>
                        <option value="Zimmatic" className="bg-slate-800 text-white" data-id="element-10454">
                          Zimmatic
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10455">
                        <ChevronDownIcon size={16} data-id="element-10456" />
                      </div>
                    </div>
                  </div>

                  <div data-id="element-10457">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10458">
                      Model
                    </label>
                    <div className="relative" data-id="element-10459">
                      <select value={model} onChange={e => setModel(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10460">
                        <option value="CP600" className="bg-slate-800 text-white" data-id="element-10461">
                          CP600
                        </option>
                        <option value="CP900" className="bg-slate-800 text-white" data-id="element-10462">
                          CP900
                        </option>
                        <option value="CP1200" className="bg-slate-800 text-white" data-id="element-10463">
                          CP1200
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10464">
                        <ChevronDownIcon size={16} data-id="element-10465" />
                      </div>
                    </div>
                  </div>

                  <div data-id="element-10466">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10467">
                      Electrical Frequency
                    </label>
                    <div className="relative" data-id="element-10468">
                      <select value={electricalFrequency} onChange={e => setElectricalFrequency(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10469">
                        <option value="60 Hz" className="bg-slate-800 text-white" data-id="element-10470">
                          60 Hz
                        </option>
                        <option value="50 Hz" className="bg-slate-800 text-white" data-id="element-10471">
                          50 Hz
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10472">
                        <ChevronDownIcon size={16} data-id="element-10473" />
                      </div>
                    </div>
                  </div>

                  <div data-id="element-10474">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10475">
                      Profile
                    </label>
                    <div className="relative" data-id="element-10476">
                      <select value={profile} onChange={e => setProfile(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10477">
                        <option value="Standard" className="bg-slate-800 text-white" data-id="element-10478">
                          Standard
                        </option>
                        <option value="Heavy" className="bg-slate-800 text-white" data-id="element-10479">
                          Heavy
                        </option>
                        <option value="Extra Heavy" className="bg-slate-800 text-white" data-id="element-10480">
                          Extra Heavy
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10481">
                        <ChevronDownIcon size={16} data-id="element-10482" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Controls Section */}
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-10483">
                <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-10484">
                  Controls
                </h3>
                <div className="space-y-5" data-id="element-10485">
                  <div data-id="element-10486">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10487">
                      Communication type
                    </label>
                    <div className="relative" data-id="element-10488">
                      <select value={communicationType} onChange={e => setCommunicationType(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10489">
                        <option value="GSM 4G" className="bg-slate-800 text-white" data-id="element-10490">
                          GSM 4G
                        </option>
                        <option value="GSM 3G" className="bg-slate-800 text-white" data-id="element-10491">
                          GSM 3G
                        </option>
                        <option value="Radio" className="bg-slate-800 text-white" data-id="element-10492">
                          Radio
                        </option>
                        <option value="WiFi" className="bg-slate-800 text-white" data-id="element-10493">
                          WiFi
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10494">
                        <ChevronDownIcon size={16} data-id="element-10495" />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-10496">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-10497">
                      <input type="checkbox" checked={temperatureSensor} onChange={e => setTemperatureSensor(e.target.checked)} className="w-4 h-4 rounded accent-blue-500" data-id="element-10498" />
                      <span className="text-white text-sm" data-id="element-10499">
                        Temperature Sensor
                      </span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-10500">
                      <input type="checkbox" checked={dosingPumpControl} onChange={e => setDosingPumpControl(e.target.checked)} className="w-4 h-4 rounded accent-blue-500" data-id="element-10501" />
                      <span className="text-white text-sm" data-id="element-10502">
                        Dosing Pump Control
                      </span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-10503">
                      <input type="checkbox" checked={stopInSlot} onChange={e => setStopInSlot(e.target.checked)} className="w-4 h-4 rounded accent-blue-500" data-id="element-10504" />
                      <span className="text-white text-sm" data-id="element-10505">
                        Stop in slot on Pivot Point
                      </span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-10506">
                      <input type="checkbox" checked={lightingArrestor} onChange={e => setLightingArrestor(e.target.checked)} className="w-4 h-4 rounded accent-blue-500" data-id="element-10507" />
                      <span className="text-white text-sm" data-id="element-10508">
                        Lighting arrestor
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Pivot Point Section */}
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-10509">
                <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-10510">
                  Pivot Point
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-10511">
                  <div data-id="element-10512">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10513">
                      Profile
                    </label>
                    <div className="relative" data-id="element-10514">
                      <select value={pivotProfile} onChange={e => setPivotProfile(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10515">
                        <option value="Standard" className="bg-slate-800 text-white" data-id="element-10516">
                          Standard
                        </option>
                        <option value="Heavy" className="bg-slate-800 text-white" data-id="element-10517">
                          Heavy
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10518">
                        <ChevronDownIcon size={16} data-id="element-10519" />
                      </div>
                    </div>
                  </div>

                  <div data-id="element-10520">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10521">
                      Protective Coating
                    </label>
                    <div className="relative" data-id="element-10522">
                      <select value={pivotProtectiveCoating} onChange={e => setPivotProtectiveCoating(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10523">
                        <option value="Galvanized" className="bg-slate-800 text-white" data-id="element-10524">
                          Galvanized
                        </option>
                        <option value="Painted" className="bg-slate-800 text-white" data-id="element-10525">
                          Painted
                        </option>
                        <option value="Stainless" className="bg-slate-800 text-white" data-id="element-10526">
                          Stainless
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10527">
                        <ChevronDownIcon size={16} data-id="element-10528" />
                      </div>
                    </div>
                  </div>

                  <div data-id="element-10529">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10530">
                      Stand Pipe Diameter
                    </label>
                    <div className="relative" data-id="element-10531">
                      <select value={standPipeDiameter} onChange={e => setStandPipeDiameter(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10532">
                        <option value='8"5/8' className="bg-slate-800 text-white" data-id="element-10533">
                          8"5/8
                        </option>
                        <option value='6"5/8' className="bg-slate-800 text-white" data-id="element-10534">
                          6"5/8
                        </option>
                        <option value="10" className="bg-slate-800 text-white" data-id="element-10535">
                          10
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10536">
                        <ChevronDownIcon size={16} data-id="element-10537" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-5" data-id="element-10538">
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-10539">
                    <input type="checkbox" checked={heavyDuty} onChange={e => setHeavyDuty(e.target.checked)} className="w-4 h-4 rounded accent-blue-500" data-id="element-10540" />
                    <span className="text-white text-sm" data-id="element-10541">Heavy duty</span>
                  </label>
                </div>
              </div>

              {/* Spans and Drive Train - Side by Side */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-10542">
                {/* Spans Section */}
                <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-10543">
                  <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-10544">
                    Spans
                  </h3>
                  <div className="space-y-5" data-id="element-10545">
                    <div data-id="element-10546">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10547">
                        Outlet Spacing
                      </label>
                      <div className="relative" data-id="element-10548">
                        <select value={outletSpacing} onChange={e => setOutletSpacing(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10549">
                          <option value="1.5 m" className="bg-slate-800 text-white" data-id="element-10550">
                            1.5 m
                          </option>
                          <option value="3 m" className="bg-slate-800 text-white" data-id="element-10551">
                            3 m
                          </option>
                          <option value="1.14 m" className="bg-slate-800 text-white" data-id="element-10552">
                            1.14 m
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10553">
                          <ChevronDownIcon size={16} data-id="element-10554" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-10555">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10556">
                        Protective Coating
                      </label>
                      <div className="relative" data-id="element-10557">
                        <select value={spansProtectiveCoating} onChange={e => setSpansProtectiveCoating(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10558">
                          <option value="Galvanized" className="bg-slate-800 text-white" data-id="element-10559">
                            Galvanized
                          </option>
                          <option value="Painted" className="bg-slate-800 text-white" data-id="element-10560">
                            Painted
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10561">
                          <ChevronDownIcon size={16} data-id="element-10562" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-10563">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10564">
                        Profile
                      </label>
                      <div className="relative" data-id="element-10565">
                        <select value={spansProfile} onChange={e => setSpansProfile(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10566">
                          <option value="Standard" className="bg-slate-800 text-white" data-id="element-10567">
                            Standard
                          </option>
                          <option value="Heavy" className="bg-slate-800 text-white" data-id="element-10568">
                            Heavy
                          </option>
                          <option value="CN+" className="bg-slate-800 text-white" data-id="element-10569">
                            CN+
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10570">
                          <ChevronDownIcon size={16} data-id="element-10571" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Drive Train Section */}
                <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-10572">
                  <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-10573">
                    Drive Train
                  </h3>
                  <div className="space-y-5" data-id="element-10574">
                    <div data-id="element-10575">
                      <label className="flex items-center gap-2 cursor-pointer mb-4" data-id="element-10576">
                        <input type="checkbox" checked={motorCover} onChange={e => setMotorCover(e.target.checked)} className="w-4 h-4 rounded accent-blue-500" data-id="element-10577" />
                        <span className="text-white text-sm" data-id="element-10578">Motor cover</span>
                      </label>
                    </div>

                    <div data-id="element-10579">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10580">
                        Center Drive
                      </label>
                      <div className="relative" data-id="element-10581">
                        <select value={centerDrive} onChange={e => setCenterDrive(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10582">
                          <option value="UMC 35/2..." className="bg-slate-800 text-white" data-id="element-10583">
                            UMC 35/2...
                          </option>
                          <option value="UMC 40/2" className="bg-slate-800 text-white" data-id="element-10584">
                            UMC 40/2
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10585">
                          <ChevronDownIcon size={16} data-id="element-10586" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-10587">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10588">
                        WD Gearbox
                      </label>
                      <div className="relative" data-id="element-10589">
                        <select value={wdGearbox} onChange={e => setWdGearbox(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10590">
                          <option value="Std. 52/1" className="bg-slate-800 text-white" data-id="element-10591">
                            Std. 52/1
                          </option>
                          <option value="Std. 40/1" className="bg-slate-800 text-white" data-id="element-10592">
                            Std. 40/1
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10593">
                          <ChevronDownIcon size={16} data-id="element-10594" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-10595">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10596">
                        Tires
                      </label>
                      <div className="relative" data-id="element-10597">
                        <select value={tires} onChange={e => setTires(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10598">
                          <option value="14.9x24" className="bg-slate-800 text-white" data-id="element-10599">
                            14.9x24
                          </option>
                          <option value="11.2x24" className="bg-slate-800 text-white" data-id="element-10600">
                            11.2x24
                          </option>
                          <option value="11.2x38" className="bg-slate-800 text-white" data-id="element-10601">
                            11.2x38
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10602">
                          <ChevronDownIcon size={16} data-id="element-10603" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Electric Cables Section */}
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-10604">
                <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-10605">
                  Electric Cables
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-10606">
                  <div data-id="element-10607">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10608">
                      Electric cable protection
                    </label>
                    <div className="relative" data-id="element-10609">
                      <select value={cableProtection} onChange={e => setCableProtection(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10610">
                        <option value="Unshield" className="bg-slate-800 text-white" data-id="element-10611">
                          Unshield
                        </option>
                        <option value="Shield" className="bg-slate-800 text-white" data-id="element-10612">
                          Shield
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10613">
                        <ChevronDownIcon size={16} data-id="element-10614" />
                      </div>
                    </div>
                  </div>

                  <div data-id="element-10615">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10616">
                      Wire size
                    </label>
                    <div className="relative" data-id="element-10617">
                      <select value={wireSize} onChange={e => setWireSize(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10618">
                        <option value="4 mm²" className="bg-slate-800 text-white" data-id="element-10619">
                          4 mm²
                        </option>
                        <option value="6 mm²" className="bg-slate-800 text-white" data-id="element-10620">
                          6 mm²
                        </option>
                        <option value="10 mm²" className="bg-slate-800 text-white" data-id="element-10621">
                          10 mm²
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10622">
                        <ChevronDownIcon size={16} data-id="element-10623" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sprinklers Section */}
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-10624">
                <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-10625">
                  Sprinklers
                </h3>

                {/* Sprinkler Preferences Subsection */}
                <div className="mb-6" data-id="element-10626">
                  <h4 className="text-sm font-medium text-blue-200 mb-4" data-id="element-10627">
                    Sprinkler Preferences
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-10628">
                    <div data-id="element-10629">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10630">
                        Max Sprinkler Spacing
                      </label>
                      <div className="flex gap-2" data-id="element-10631">
                        <input type="number" step="0.01" value={maxSprinklerSpacing} onChange={e => setMaxSprinklerSpacing(e.target.value)} className="flex-1 bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="1.50" data-id="element-10632" />
                        <span className="flex items-center text-blue-300 text-sm px-2" data-id="element-10633">
                          m
                        </span>
                      </div>
                    </div>

                    <div data-id="element-10634">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10635">
                        Name
                      </label>
                      <div className="relative" data-id="element-10636">
                        <select value={sprinklerName} onChange={e => setSprinklerName(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10637">
                          <option value="Nelson | Rotator-R3..." className="bg-slate-800 text-white" data-id="element-10638">
                            Nelson | Rotator-R3...
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10639">
                          <ChevronDownIcon size={16} data-id="element-10640" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-10641">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10642">
                        Pad
                      </label>
                      <div className="relative" data-id="element-10643">
                        <select value={pad} onChange={e => setPad(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10644">
                          <option value="Orange" className="bg-slate-800 text-white" data-id="element-10645">
                            Orange
                          </option>
                          <option value="Blue" className="bg-slate-800 text-white" data-id="element-10646">
                            Blue
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10647">
                          <ChevronDownIcon size={16} data-id="element-10648" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-10649">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10650">
                        Drops
                      </label>
                      <div className="relative" data-id="element-10651">
                        <select value={drops} onChange={e => setDrops(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10652">
                          <option value="Flex Drop with plast..." className="bg-slate-800 text-white" data-id="element-10653">
                            Flex Drop with plast...
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10654">
                          <ChevronDownIcon size={16} data-id="element-10655" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-10656">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10657">
                        Clearance height
                      </label>
                      <div className="flex gap-2" data-id="element-10658">
                        <input type="number" step="0.01" value={clearanceHeight} onChange={e => setClearanceHeight(e.target.value)} className="flex-1 bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10659" />
                        <span className="flex items-center text-blue-300 text-sm px-2" data-id="element-10660">
                          m
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-white/10 my-6" data-id="element-10661"></div>

                {/* Regulator Subsection */}
                <div data-id="element-10662">
                  <label className="flex items-center gap-2 cursor-pointer mb-4" data-id="element-10663">
                    <input type="checkbox" checked={regulator} onChange={e => setRegulator(e.target.checked)} className="w-4 h-4 rounded accent-blue-500" data-id="element-10664" />
                    <h4 className="text-sm font-medium text-blue-200" data-id="element-10665">
                      Regulator
                    </h4>
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-10666">
                    <div data-id="element-10667">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10668">
                        Brand
                      </label>
                      <div className="relative" data-id="element-10669">
                        <select value={regulatorBrand} onChange={e => setRegulatorBrand(e.target.value)} disabled={!regulator} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10670">
                          <option value="Komet" className="bg-slate-800 text-white" data-id="element-10671">
                            Komet
                          </option>
                          <option value="Nelson" className="bg-slate-800 text-white" data-id="element-10672">
                            Nelson
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10673">
                          <ChevronDownIcon size={16} data-id="element-10674" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-10675">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10676">
                        Nominal pressure
                      </label>
                      <div className="relative" data-id="element-10677">
                        <select value={nominalPressure} onChange={e => setNominalPressure(e.target.value)} disabled={!regulator} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10678">
                          <option value="10 psi" className="bg-slate-800 text-white" data-id="element-10679">
                            10 psi
                          </option>
                          <option value="15 psi" className="bg-slate-800 text-white" data-id="element-10680">
                            15 psi
                          </option>
                          <option value="6 psi" className="bg-slate-800 text-white" data-id="element-10681">
                            6 psi
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10682">
                          <ChevronDownIcon size={16} data-id="element-10683" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-10684">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10685">
                        Vertical position
                      </label>
                      <div className="relative" data-id="element-10686">
                        <select value={verticalPosition} onChange={e => setVerticalPosition(e.target.value)} disabled={!regulator} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10687">
                          <option value="Top" className="bg-slate-800 text-white" data-id="element-10688">
                            Top
                          </option>
                          <option value="Bottom" className="bg-slate-800 text-white" data-id="element-10689">
                            Bottom
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10690">
                          <ChevronDownIcon size={16} data-id="element-10691" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* End Guns and Booster Pump - Side by Side */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-10692">
                {/* End Gun 1 */}
                <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-10693">
                  <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-10694">
                    End Gun 1
                  </h3>
                  <div className="space-y-5" data-id="element-10695">
                    <div data-id="element-10696">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10697">
                        Brand
                      </label>
                      <div className="relative" data-id="element-10698">
                        <select value={endGun1Brand} onChange={e => setEndGun1Brand(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10699">
                          <option value="Komet" className="bg-slate-800 text-white" data-id="element-10700">
                            Komet
                          </option>
                          <option value="Nelson" className="bg-slate-800 text-white" data-id="element-10701">
                            Nelson
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10702">
                          <ChevronDownIcon size={16} data-id="element-10703" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-10704">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10705">
                        Model
                      </label>
                      <div className="relative" data-id="element-10706">
                        <select value={endGun1Model} onChange={e => setEndGun1Model(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10707">
                          <option value="KPW HEAD-UP" className="bg-slate-800 text-white" data-id="element-10708">
                            KPW HEAD-UP
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10709">
                          <ChevronDownIcon size={16} data-id="element-10710" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-10711">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10712">
                        Control
                      </label>
                      <div className="relative" data-id="element-10713">
                        <select value={endGun1Control} onChange={e => setEndGun1Control(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10714">
                          <option value="Endgun Control wit..." className="bg-slate-800 text-white" data-id="element-10715">
                            Endgun Control wit...
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10716">
                          <ChevronDownIcon size={16} data-id="element-10717" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* End Gun 2 */}
                <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-10718">
                  <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-10719">
                    End Gun 2
                  </h3>
                  <div className="space-y-5" data-id="element-10720">
                    <div data-id="element-10721">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10722">
                        Brand
                      </label>
                      <div className="relative" data-id="element-10723">
                        <select value={endGun2Brand} onChange={e => setEndGun2Brand(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10724">
                          <option value="" className="bg-slate-800 text-blue-300" data-id="element-10725">
                            Select brand
                          </option>
                          <option value="Komet" className="bg-slate-800 text-white" data-id="element-10726">
                            Komet
                          </option>
                          <option value="Nelson" className="bg-slate-800 text-white" data-id="element-10727">
                            Nelson
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10728">
                          <ChevronDownIcon size={16} data-id="element-10729" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-10730">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10731">
                        Model
                      </label>
                      <div className="relative" data-id="element-10732">
                        <select value={endGun2Model} onChange={e => setEndGun2Model(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10733">
                          <option value="" className="bg-slate-800 text-blue-300" data-id="element-10734">
                            Select model
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10735">
                          <ChevronDownIcon size={16} data-id="element-10736" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-10737">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10738">
                        Control
                      </label>
                      <div className="relative" data-id="element-10739">
                        <select value={endGun2Control} onChange={e => setEndGun2Control(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10740">
                          <option value="" className="bg-slate-800 text-blue-300" data-id="element-10741">
                            Select control
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10742">
                          <ChevronDownIcon size={16} data-id="element-10743" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Booster Pump */}
                <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-10744">
                  <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-10745">
                    Booster Pump
                  </h3>
                  <div className="space-y-5" data-id="element-10746">
                    <div data-id="element-10747">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10748">
                        Power
                      </label>
                      <div className="relative" data-id="element-10749">
                        <select value={boosterPumpPower} onChange={e => setBoosterPumpPower(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10750">
                          <option value="" className="bg-slate-800 text-blue-300" data-id="element-10751">
                            Select power
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10752">
                          <ChevronDownIcon size={16} data-id="element-10753" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Save Button for Design Preferences */}
              <div className="flex items-center justify-end pt-2" data-id="element-10754">
                <button onClick={handleSaveDesignPreferences} disabled={savingTab === 'design'} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white rounded-lg px-5 py-2.5 text-sm font-medium transition-colors shadow-lg shadow-blue-900/20" data-id="element-10755">
                  {savingTab === 'design' ? <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-10756" />
                      Saving...
                    </> : savedTab === 'design' ? <>
                      <CheckCircleIcon size={16} data-id="element-10757" />
                      Saved!
                    </> : <>
                      <SaveIcon size={16} data-id="element-10758" />
                      Save Design Preferences
                    </>}
                </button>
              </div>
            </div>}

          {/* Quote Tab */}
          {activeTab === 'quote' && <div className="space-y-6" data-id="element-10759">
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-10760">
                <h3 className="text-white font-semibold text-base mb-2 pb-3 border-b border-white/10" data-id="element-10761">
                  Dealer Mark-up
                </h3>
                <p className="text-blue-300/70 text-sm mb-6" data-id="element-10762">
                  This is the default margin percentage applied to all quotes
                  generated for your customers. When a new quote is created,
                  this mark-up will be automatically added on top of the base
                  price to calculate the final customer price. You can also
                  configure a different mark-up for each individual customer,
                  and you can still adjust it on individual quotes if needed.
                </p>
                <div data-id="element-10763">
                  <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10764">
                    Default mark-up <span className="text-red-400" data-id="element-10765">*</span>
                  </label>
                  <div className="relative w-48" data-id="element-10766">
                    <input type="number" step="0.01" value={dealerMarkup} onChange={e => setDealerMarkup(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 pr-10 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="25.00" data-id="element-10767" />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none text-blue-300" data-id="element-10768">
                      %
                    </div>
                  </div>
                </div>
              </div>

              {/* Save Button for Quote */}
              <div className="flex items-center justify-end pt-2" data-id="element-10769">
                <button onClick={handleSaveQuote} disabled={savingTab === 'quote'} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white rounded-lg px-5 py-2.5 text-sm font-medium transition-colors shadow-lg shadow-blue-900/20" data-id="element-10770">
                  {savingTab === 'quote' ? <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-10771" />
                      Saving...
                    </> : savedTab === 'quote' ? <>
                      <CheckCircleIcon size={16} data-id="element-10772" />
                      Saved!
                    </> : <>
                      <SaveIcon size={16} data-id="element-10773" />
                      Save Quote Settings
                    </>}
                </button>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}